import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { ChevronLeft, ChevronRight, Sparkles } from "lucide-react";
import StyleQuiz from "./StyleQuiz";

const SLIDE_DEFS = [
  { kind: "story", key: "slide_1", img: "https://www.fabricadeprofile.ro/wp-content/uploads/2022/05/interior-cornisa01.jpg" },
  { kind: "story", key: "slide_2", img: "https://www.fabricadeprofile.ro/wp-content/uploads/2022/05/interior-rozete-plafon01.jpg" },
  { kind: "story", key: "slide_3", img: "https://www.fabricadeprofile.ro/wp-content/uploads/2022/05/exterior-cornisa01.jpg" },
  { kind: "process", key: "slide_4" },
  { kind: "tip", key: "slide_5" },
  { kind: "quiz", key: "slide_6" },
];

export default function WaitingPresentation({
  simType,
  style,
  mockTimeSec = 60,
  simulationId,
  generatedCount = 0,
  requestedCount = 0,
}) {
  const { t } = useTranslation();
  const [slide, setSlide] = useState(0);
  const [catalog, setCatalog] = useState([]);
  const [elapsed, setElapsed] = useState(0);
  const [autoplay, setAutoplay] = useState(true);

  // Translated slides (rebuilt when language changes).
  const SLIDES = useMemo(
    () =>
      SLIDE_DEFS.map((s) => ({
        ...s,
        eyebrow: t(`wait.${s.key}_eyebrow`),
        title: t(`wait.${s.key}_title`),
        body: t(`wait.${s.key}_body`),
      })),
    [t],
  );

  // Auto-advance every 7s; pause when landing on quiz slide
  useEffect(() => {
    if (!autoplay) return;
    const t = setInterval(() => {
      setSlide((s) => {
        const next = (s + 1) % SLIDES.length;
        if (SLIDES[next].kind === "quiz") setAutoplay(false);
        return next;
      });
    }, 7000);
    return () => clearInterval(t);
  }, [autoplay, SLIDES]);

  useEffect(() => {
    const t = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!simType) return;
    api
      .get(`/catalog?sim_type=${simType}`)
      .then((r) => setCatalog(r.data.categories.filter((c) => c.image).slice(0, 8)))
      .catch(() => {});
  }, [simType]);

  const cur = SLIDES[slide];
  const hasReal = requestedCount > 0;
  const realPct = hasReal
    ? Math.min(95, Math.round((generatedCount / requestedCount) * 100))
    : 0;
  const timePct = Math.min(95, Math.round((elapsed / mockTimeSec) * 100));
  const pct = hasReal && generatedCount > 0 ? realPct : Math.max(timePct, realPct);
  const minutes = Math.floor(elapsed / 60);
  const seconds = elapsed % 60;

  let etaSec = null;
  if (hasReal && generatedCount > 0 && generatedCount < requestedCount) {
    const observedPerImage = elapsed / generatedCount;
    const remaining = requestedCount - generatedCount;
    etaSec = Math.max(0, Math.min(600, Math.round(observedPerImage * remaining)));
  } else if (hasReal && generatedCount === 0 && mockTimeSec > elapsed) {
    etaSec = Math.max(0, Math.round(mockTimeSec - elapsed));
  }
  const etaMin = etaSec != null ? Math.floor(etaSec / 60) : 0;
  const etaS = etaSec != null ? etaSec % 60 : 0;
  const etaLabel =
    etaSec == null
      ? null
      : etaSec < 60
      ? t("wait.eta_remaining", { n: etaSec })
      : t("wait.eta_remaining_minutes", { m: etaMin, s: etaS.toString().padStart(2, "0") });

  return (
    <div
      className="border border-white/10 rounded-lg bg-zinc-900/30 overflow-hidden"
      data-testid="waiting-presentation"
    >
      {/* Header with progress */}
      <div className="px-6 pt-6">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-orange-500">
            <Sparkles size={12} className="animate-pulse" />
            {t("wait.header_eyebrow")} · {style?.replace("_", " ")}
          </div>
          <div className="text-xs text-zinc-400 font-mono tabular-nums">
            {minutes.toString().padStart(2, "0")}:{seconds.toString().padStart(2, "0")}
            {etaLabel && (
              <span
                className="text-emerald-400 ml-2"
                data-testid="eta-estimate"
                title={t("wait.eta_tooltip")}
              >
                · {etaLabel}
              </span>
            )}
            {!etaLabel && mockTimeSec > 0 && (
              <span className="text-zinc-600 ml-2">
                / ~{Math.round(mockTimeSec)}s
              </span>
            )}
          </div>
        </div>
        {/* PROMINENT progress indicator: "Imagine X/Y generată" */}
        {hasReal && (
          <div
            className="flex items-baseline gap-2 mb-3"
            data-testid="progress-counter"
          >
            <span className="font-outfit font-black text-white text-3xl tabular-nums">
              {generatedCount}
              <span className="text-zinc-600">/{requestedCount}</span>
            </span>
            <span className="text-sm text-zinc-400">
              {generatedCount === 0
                ? t("wait.progress_starting")
                : generatedCount < requestedCount
                ? t("wait.progress_in_progress")
                : t("wait.progress_finalizing")}
            </span>
          </div>
        )}
        <div className="h-2 bg-zinc-800 rounded-full overflow-hidden mb-4 relative">
          <div
            className="h-full bg-gradient-to-r from-orange-600 to-orange-400 transition-all duration-700 ease-out"
            style={{ width: `${pct}%` }}
            data-testid="progress-bar-fill"
          />
          {/* Animated shimmer overlay while in progress */}
          {pct > 0 && pct < 95 && (
            <div
              className="absolute inset-y-0 left-0 w-1/4 bg-gradient-to-r from-transparent via-white/15 to-transparent"
              style={{
                animation: "shimmer 1.6s linear infinite",
                transform: `translateX(${pct * 3}%)`,
              }}
            />
          )}
        </div>
      </div>

      {/* Slide content */}
      <div className="grid md:grid-cols-2 gap-0 min-h-[340px]">
        {cur.kind === "quiz" ? (
          <div className="md:col-span-2 p-6 md:p-8">
            <StyleQuiz simulationId={simulationId} currentStyle={style} />
          </div>
        ) : (
          <>
            {cur.img ? (
              <div className="relative bg-zinc-950 overflow-hidden order-2 md:order-1">
                <img
                  key={cur.img}
                  src={cur.img}
                  alt=""
                  className="absolute inset-0 w-full h-full object-cover animate-fade-in-up"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
              </div>
            ) : (
              <div className="relative bg-gradient-to-br from-orange-500/10 via-zinc-900 to-zinc-950 order-2 md:order-1 flex items-center justify-center">
                <div className="text-orange-500/30 font-cormorant italic text-9xl select-none">
                  0{slide + 1}
                </div>
              </div>
            )}

            <div className="p-8 md:p-10 flex flex-col justify-center order-1 md:order-2">
              <div key={slide} className="animate-fade-in-up">
                <div className="text-xs uppercase tracking-[0.3em] text-orange-500 mb-3">
                  {cur.eyebrow}
                </div>
                <h2 className="font-outfit font-black text-white text-2xl md:text-3xl tracking-tighter leading-tight mb-4">
                  {cur.title}
                </h2>
                <p className="text-zinc-300 leading-relaxed text-sm md:text-base">
                  {cur.body}
                </p>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Slide navigation */}
      <div className="px-6 md:px-10 pb-6 pt-2">
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              setAutoplay(false);
              setSlide((s) => (s - 1 + SLIDES.length) % SLIDES.length);
            }}
            className="w-9 h-9 rounded-full border border-white/10 flex items-center justify-center text-zinc-400 hover:text-white hover:border-orange-500 transition"
            data-testid="presentation-prev"
            aria-label={t("wait.slide_prev")}
          >
            <ChevronLeft size={16} />
          </button>
          <div className="flex-1 flex items-center justify-center gap-1.5">
            {SLIDES.map((s, i) => (
              <button
                key={s.key}
                onClick={() => {
                  setAutoplay(false);
                  setSlide(i);
                }}
                className={`h-1.5 rounded-full transition-all ${
                  i === slide
                    ? s.kind === "quiz"
                      ? "bg-orange-500 w-12"
                      : "bg-orange-500 w-8"
                    : s.kind === "quiz"
                    ? "bg-orange-500/50 w-3 hover:bg-orange-500"
                    : "bg-zinc-700 w-2 hover:bg-zinc-500"
                }`}
                data-testid={`presentation-dot-${i}`}
                aria-label={`Slide ${i + 1}`}
                title={s.kind === "quiz" ? t("wait.slide_quiz_tooltip") : s.title}
              />
            ))}
          </div>
          <button
            onClick={() => {
              setAutoplay(false);
              setSlide((s) => (s + 1) % SLIDES.length);
            }}
            className="w-9 h-9 rounded-full border border-white/10 flex items-center justify-center text-zinc-400 hover:text-white hover:border-orange-500 transition"
            data-testid="presentation-next"
            aria-label={t("wait.slide_next")}
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      {/* Catalog ribbon */}
      {catalog.length > 0 && (
        <div className="border-t border-white/5 px-6 py-4">
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-3">
            {t("wait.catalog_ribbon", { type: simType?.toLowerCase() })}
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {catalog.map((c) => (
              <div
                key={c.slug}
                className="shrink-0 w-32 border border-white/5 rounded-md overflow-hidden bg-zinc-950"
                title={c.name}
              >
                <div className="h-16 overflow-hidden">
                  <img
                    src={c.image}
                    alt={c.name}
                    className="w-full h-full object-cover hover:scale-110 transition-transform duration-700"
                    onError={(e) => (e.currentTarget.style.display = "none")}
                  />
                </div>
                <div className="p-2 text-[11px] text-zinc-300 truncate font-medium">
                  {c.name}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
